/* eslint-disable react/react-in-jsx-scope */
import { Tooltip } from "@material-ui/core";
import { Column } from "components/Table/Table";
import { DATE_FORMAT_PRINT } from "config/constants";
import useText from "constants/text";
import { Evolution, LinearEvulotionRequest, StatusContainerRequest } from "domains/Request/component/shared/MainStyles";
import { extractRegions } from "domains/Request/component/Tour/utils";
import { StatusSpan } from "domains/Request/data";
import {
  Course,
  CourseStatus,
  ERequestType,
  Mission,
  MissionStatusType,
  TaskMission,
  TaskMissionStatus,
  TemplateStop,
} from "generated/graphql";
import moment from "moment";
import { truncateString } from "utils";

export const displayAreas = (templateStops: Omit<TemplateStop, "__typename">[]) =>
  extractRegions(templateStops)?.join(", ") || "N/A";

const getMissionStatus = (tasks: TaskMission[], type: ERequestType, status: any) => {
  if (type === ERequestType.Course) {
    if ([CourseStatus.OnGoing, CourseStatus.Assigned].includes(status)) return MissionStatusType.InProgress;
    if (status === CourseStatus.Done) return MissionStatusType.Done;
    return MissionStatusType.NotStarted;
  }

  if (type === ERequestType.Tr) {
    const Allstops = tasks?.map(({ stops }) => stops).flat();
    if (Allstops?.every((stop) => stop?.status === MissionStatusType.Done)) return MissionStatusType.Done;
    if (Allstops.find((stop) => [MissionStatusType.Assigned, MissionStatusType.Done].includes(stop?.status)))
      return MissionStatusType.InProgress;
  }
  if (type === "MAD") {
    if ([TaskMissionStatus.OnGoing, TaskMissionStatus.Assigned, TaskMissionStatus.Started].includes(status))
      return MissionStatusType.InProgress;
    if (status === TaskMissionStatus.Done) return MissionStatusType.Done;
    return MissionStatusType.NotStarted;
  }
  if (tasks?.every((task) => task?.status === TaskMissionStatus.Done)) return MissionStatusType.Done;
  if (
    tasks?.find((task) =>
      [
        TaskMissionStatus.Done,
        TaskMissionStatus.Started,
        TaskMissionStatus.Assigned,
        TaskMissionStatus.OnGoing,
      ].includes(task?.status)
    )
  )
    return MissionStatusType.InProgress;

  return MissionStatusType.NotStarted;
};

export const taskColumns = (): Column<Partial<any>>[] => {
  const statusColor = {
    [MissionStatusType.NotStarted]: "#399ebf",
    [MissionStatusType.InProgress]: "rgb(249, 148, 72)",
    [MissionStatusType.Done]: "rgb(50,212,21)",
  };

  const getRequestEvolution = {
    [MissionStatusType.NotStarted]: "33%",
    [MissionStatusType.InProgress]: "66%",
    [MissionStatusType.Done]: "100%",
  };

  const missionsStatusColor = {
    [MissionStatusType.NotStarted]: "57, 158, 191",
    [MissionStatusType.InProgress]: "249, 148, 72",
    [MissionStatusType.Done]: "50, 212, 21",
  };
  const { text } = useText();
  return [
    {
      title: `${text.all.task_id}`,
      field: "uid",
      render: ({ uid, task_UID }) => <div>{uid || task_UID}</div>,
    },
    {
      title: `${text.all.areas}`,
      field: "address",
      render: ({ type: missionType, stops, axes, task_subtasks, task_type }) => {
        const stopsToDisplay = [ERequestType.Msg, ERequestType.Tr].includes(missionType)
          ? axes?.map((axe: any) => axe?.stops)?.flat()
          : stops;
        const address =
          task_type === "MAD" ? task_subtasks?.[0]?.subtask_address?.region || "N/A" : displayAreas(stopsToDisplay);
        return (
          <Tooltip title={address}>
            <div>{truncateString(address, 20)}</div>
          </Tooltip>
        );
      },
    },
    {
      title: `${text.all.date}`,
      field: "executionDate",
      render: ({ executionDate }) => moment(executionDate).format(DATE_FORMAT_PRINT),
    },
    {
      field: "status",
      title: `${text.all.status}`,
      render: ({ status, tasks, type = ERequestType.Course, task_type, task_status }) => {
        return (
          <StatusContainerRequest>
            <StatusSpan color={statusColor[getMissionStatus(tasks, task_type || type, task_status || status)]}>
              {getMissionStatus(tasks, task_type || type, task_status || status)?.replace(/_/g, " ")}
            </StatusSpan>
            <LinearEvulotionRequest>
              <Evolution
                width={getRequestEvolution[getMissionStatus(tasks, task_type || type, task_status || status)]}
                background-color={
                  missionsStatusColor[getMissionStatus(tasks, task_type || type, task_status || status)]
                }
              />
            </LinearEvulotionRequest>
          </StatusContainerRequest>
        );
      },
    },
  ];
};

export const subtaskActions = (text: any) => {
  const actions = [
    {
      key: "edit",
      label: text.all.edit,
    },
    {
      key: "delete",
      label: text.all.delete,
    },
  ];
  return actions;
};
